import {
  Add as AddIcon,
  Cancel as CancelIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ShoppingCart as OrderIcon,
  Save as SaveIcon,
  LocalShipping as ShippingIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Divider,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

import Price from "../../common/Price";
import { useAdminOrderItems } from "../../../hooks/useAdminOrderItems";
import { ItemStatus, OrderItemDetail } from "../../../types/admin";

interface OrderItemCardProps {
  item: OrderItemDetail;
  itemStatuses: ItemStatus[];
  onUpdate: () => void;
}

export const OrderItemCard: React.FC<OrderItemCardProps> = ({
  item,
  itemStatuses,
  onUpdate,
}) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [trackingNumber, setTrackingNumber] = useState(
    item.trackingNumber || "",
  );
  const [notes, setNotes] = useState(item.notes || "");
  const [selectedStatusId, setSelectedStatusId] = useState(
    item.itemStatusId || 1,
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { updateOrderItem, addTrackingNumber, markAsOrdered, markAsShipped } =
    useAdminOrderItems();

  const currentStatus = itemStatuses.find((s) => s.id === item.itemStatusId);
  const nextAllowedStatuses = itemStatuses.filter((s) =>
    currentStatus?.allowedNextStatusIds?.includes(s.id),
  );

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      const success = await updateOrderItem({
        orderItemId: item.id,
        itemStatusId: selectedStatusId,
        notes: notes.trim() || undefined,
        trackingNumber: trackingNumber.trim() || undefined,
      });

      if (success) {
        setEditing(false);
        onUpdate();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : t("admin.orders.failedToUpdateItem"));
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    setTrackingNumber(item.trackingNumber || "");
    setNotes(item.notes || "");
    setSelectedStatusId(item.itemStatusId || 1);
    setError(null);
  };

  const handleMarkAsOrdered = async () => {
    try {
      setLoading(true);
      setError(null);

      const success = await markAsOrdered(item.id, notes.trim() || undefined);
      if (success) {
        onUpdate();
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("admin.orders.failedToMarkOrdered"),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsShipped = async () => {
    if (!trackingNumber.trim()) {
      setError(t("admin.orders.trackingNumberRequired"));
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const success = await markAsShipped(item.id, {
        trackingNumber: trackingNumber.trim(),
        notes: notes.trim() || undefined,
      });

      if (success) {
        onUpdate();
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : t("admin.orders.failedToMarkShipped"),
      );
    } finally {
      setLoading(false);
    }
  };

  const getRetailerColor = (retailer?: string) => {
    switch (retailer?.toLowerCase()) {
      case "amazon":
        return "#ff9900";
      case "walmart":
        return "#007dc6";
      case "costco":
        return "#e31837";
      default:
        return "#666";
    }
  };

  const getStatusColor = (status?: ItemStatus) => {
    return status?.color || "#666";
  };

  return (
    <Card sx={{ mb: 2, border: "1px solid #e0e0e0" }}>
      <CardContent>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <img
              src={item.image || "/placeholder-product.png"}
              alt={item.title}
              style={{
                width: 60,
                height: 60,
                objectFit: "cover",
                borderRadius: 8,
              }}
            />
            <Box>
              <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                {item.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t("admin.orders.qty")}: {item.quantity} ×{" "}
                <Price value={item.price} />
              </Typography>
            </Box>
          </Box>

          <Box display="flex" alignItems="center" gap={1}>
            {item.retailer && (
              <Chip
                label={item.retailer}
                size="small"
                sx={{
                  backgroundColor: getRetailerColor(item.retailer),
                  color: "white",
                  fontWeight: 600,
                }}
              />
            )}

            {currentStatus && (
              <Chip
                label={currentStatus.displayName}
                size="small"
                sx={{
                  backgroundColor: getStatusColor(currentStatus),
                  color: "white",
                  fontWeight: 600,
                }}
              />
            )}

            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              sx={{
                transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
                transition: "transform 0.2s",
              }}
            >
              <ExpandMoreIcon />
            </IconButton>
          </Box>
        </Box>

        <Collapse in={expanded}>
          <Divider sx={{ my: 2 }} />

          {error && (
            <Alert
              severity="error"
              sx={{ mb: 2 }}
              onClose={() => setError(null)}
            >
              {error}
            </Alert>
          )}

          {editing ? (
            <Box>
              <Stack spacing={2} mb={2}>
                <TextField
                  label={t("admin.orders.status")}
                  select
                  value={selectedStatusId}
                  onChange={(e) => setSelectedStatusId(Number(e.target.value))}
                  fullWidth
                  size="small"
                >
                  {itemStatuses.map((status) => (
                    <option key={status.id} value={status.id}>
                      {status.displayName}
                    </option>
                  ))}
                </TextField>

                <TextField
                  label={t("admin.orders.trackingNumber")}
                  value={trackingNumber}
                  onChange={(e) => setTrackingNumber(e.target.value)}
                  fullWidth
                  size="small"
                  placeholder={t("admin.orders.enterTrackingNumber")}
                />

                <TextField
                  label={t("admin.orders.notes")}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  placeholder={t("admin.orders.addNotesPlaceholder")}
                />
              </Stack>

              <Stack direction="row" spacing={1}>
                <Button
                  variant="contained"
                  onClick={handleSave}
                  disabled={loading}
                  startIcon={<SaveIcon />}
                  size="small"
                >
                  {t("common.save")}
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                  disabled={loading}
                  startIcon={<CancelIcon />}
                  size="small"
                >
                  {t("common.cancel")}
                </Button>
              </Stack>
            </Box>
          ) : (
            <Box>
              <Stack spacing={2} mb={2}>
                {item.trackingNumber && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <ShippingIcon color="action" />
                    <Typography variant="body2">
                      {t("admin.orders.tracking")}: {item.trackingNumber}
                    </Typography>
                  </Box>
                )}

                {item.notes && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      {t("admin.orders.notes")}: {item.notes}
                    </Typography>
                  </Box>
                )}

                {item.sellerName && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2" color="text.secondary">
                      {t("admin.orders.seller")}: <strong>{item.sellerName}</strong>
                      {item.sellerShippingFee != null && item.sellerShippingFee > 0 && (
                        <> · <Price value={item.sellerShippingFee} /> {t("admin.orders.sellerShipFee")}</>
                      )}
                    </Typography>
                  </Box>
                )}

                {item.orderedAt && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <OrderIcon color="action" />
                    <Typography variant="body2">
                      {t("admin.orders.ordered")}: {new Date(item.orderedAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}

                {item.shippedAt && (
                  <Box display="flex" alignItems="center" gap={1}>
                    <ShippingIcon color="action" />
                    <Typography variant="body2">
                      {t("admin.orders.shipped")}: {new Date(item.shippedAt).toLocaleDateString()}
                    </Typography>
                  </Box>
                )}
              </Stack>

              <Stack direction="row" spacing={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  onClick={() => setEditing(true)}
                  startIcon={<EditIcon />}
                  size="small"
                >
                  {t("common.edit")}
                </Button>

                {!item.isOrdered && (
                  <Button
                    variant="outlined"
                    onClick={handleMarkAsOrdered}
                    disabled={loading}
                    startIcon={<OrderIcon />}
                    size="small"
                    color="success"
                  >
                    {t("admin.orders.markOrdered")}
                  </Button>
                )}

                {item.isOrdered && !item.isShipped && (
                  <Button
                    variant="outlined"
                    onClick={handleMarkAsShipped}
                    disabled={loading || !trackingNumber.trim()}
                    startIcon={<ShippingIcon />}
                    size="small"
                    color="primary"
                  >
                    {t("admin.orders.markShipped")}
                  </Button>
                )}

                {nextAllowedStatuses.length > 0 && (
                  <Button
                    variant="outlined"
                    onClick={() => setEditing(true)}
                    startIcon={<AddIcon />}
                    size="small"
                    color="info"
                  >
                    {t("admin.orders.changeStatus")}
                  </Button>
                )}
              </Stack>
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};
